// System Admin (CR-066 "SSO, permissions engine, audit engine, credentials remain system-admin controlled."; CR-065;
// B10 plan D14, slice S20b; acceptance A-40, A-41). `#settings/system/<area>`, the Admin only (DC-228: the one Admin
// sees the console and System Admin): the route is gated on the reads only that seat holds (`admin.verify_ledger`,
// `admin.adapters`, `admin.secrets` — register.ts), so any other reader is shown the not-found screen here, and the
// server refuses every one of those reads to them anyway (server-side authorization; the gate only hides).
//
// Five sections, as route tabs: *Permissions engine* (the Master Permission Matrix over `permissions.matrix`,
// read-only), *Audit engine* (`audit.query` in his seven fields, plus *Validate* over `admin.verify_ledger`, the hash
// chain recomputed), *SSO* (the identity port from `admin.adapters`, read-only), *Credentials* (`admin.secrets` —
// names, presence and source, never a value — and the adapters, read-only) and *Integrations* (B11's
// `admin.integrations`, drawn for a reader who holds it). Nothing here writes, except the Integrations section's
// *Retry* / *Manual recovery*, which are B11's tools.

import { useState, type FormEvent, type ReactNode } from "react";
import {
  ACCESS_LABELS, ACTION_LABELS, Banner, Button, Card, EmptyState, ERROR_LABELS, Field, Flex, G6_LABELS, History, HISTORY_LABELS,
  INTAKE_STATUS, Input, KeyValue, PageHeader, Pill, Stack, Table, Tabs, Td, Th, Tr, formatDateTime, type HistoryArtifact, type HistoryEvent, type TabsItem,
} from "@/mc";
import {
  adminAdapters, adminSecrets, adminVerifyLedger, auditQuery, type AdapterRow, type AdminVerifyLedgerOutput, type AuditQueryInput,
  type AuditRow, type SecretRow,
} from "@/api/tools";
import { errorText, useRead } from "@/api/useTool";
import { useSession } from "@/session/useSession";
import type { Route } from "@/router";
import { V1Slot } from "@/shell/island";
import { NotFound } from "@/views/NotFound";
import { IntegrationsView } from "./IntegrationsView";
import { PermissionMatrixView, useMatrix } from "./PermissionMatrixPage";
import { AUDIT_ENGINE_LABELS, SYSTEM_LABELS } from "./labels";
import { hsKey } from "@/views/b10/lib/hsKey";

// ---- the sections ---------------------------------------------------------------------------------------------------

export const SYSTEM_AREAS = ["permissions", "audit", "sso", "credentials", "integrations"] as const;
export type SystemArea = (typeof SYSTEM_AREAS)[number];

export const SYSTEM_AREA_LABEL: Readonly<Record<SystemArea, string>> = {
  permissions: SYSTEM_LABELS.permissionsEngine,
  audit: SYSTEM_LABELS.auditEngine,
  sso: SYSTEM_LABELS.sso,
  credentials: SYSTEM_LABELS.credentials,
  integrations: SYSTEM_LABELS.integrations,
};

/** The section's screen. */
export function systemHref(area: SystemArea): string {
  return `#settings/system/${area}`;
}

/** The section a route asks for: `#settings/system` opens the Permissions engine; an unknown section is null. */
export function systemAreaOf(route: Pick<Route, "view" | "parts">): SystemArea | null {
  if (route.view === "admin" && route.parts[0] === "integrations") return "integrations";
  const part = route.parts[1];
  if (part === undefined) return "permissions";
  return (SYSTEM_AREAS as readonly string[]).includes(part) ? (part as SystemArea) : null;
}

/** The tabs a reader is offered: *Integrations* only when B11's `admin.integrations` is in their manifest. */
export function systemTabs(canIntegrations: boolean): TabsItem[] {
  return SYSTEM_AREAS.filter((area) => area !== "integrations" || canIntegrations).map((area) => ({
    key: area,
    label: SYSTEM_AREA_LABEL[area],
    href: systemHref(area),
    hs: hsKey("cfg.sys", area),
  }));
}

// ---- Audit engine ---------------------------------------------------------------------------------------------------

type Change = { before?: unknown; after?: unknown };

const isChange = (value: unknown): value is Change =>
  typeof value === "object" && value !== null && ("before" in value || "after" in value);

function shown(value: unknown): string {
  if (value === null || value === undefined) return "—";
  return typeof value === "string" ? value : JSON.stringify(value);
}

/** One side of a receipt's changes, field by field (`field: value · …`), at most six fields. */
export function changeSide(changes: Record<string, unknown> | null | undefined, side: "before" | "after"): string {
  const entries = Object.entries(changes ?? {}).slice(0, 6);
  if (entries.length === 0) return "—";
  return entries.map(([field, value]) => `${field}: ${shown(isChange(value) ? value[side] : side === "after" ? value : null)}`).join(" · ");
}

/** A requisition id goes to `related_requisition_id`, anything else to `related_candidate_id`. */
export function relatedFilter(value: string): Pick<AuditQueryInput, "related_requisition_id" | "related_candidate_id"> {
  const id = value.trim();
  if (!id) return {};
  return /^R-\d+$/.test(id) ? { related_requisition_id: id } : { related_candidate_id: id };
}

/** One ledger row in his seven fields (DC-218: audit rows are one shape everywhere, DC-20's `hist` audit form): Old →
 * New, Actor, Timestamp, Reason, Related req / candidate, Evidence and Source. */
export function auditEvent(row: AuditRow): HistoryEvent {
  const artifacts: HistoryArtifact[] = [];
  if (row.related_requisition_id) artifacts.push({ label: row.related_requisition_id, href: `#requisitions/${row.related_requisition_id}` });
  if (row.related_candidate_id) artifacts.push({ label: row.related_candidate_id });
  if (row.evidence_ref) artifacts.push({ label: row.evidence_ref });
  const changed = row.changes !== null && Object.keys(row.changes).length > 0;
  return {
    id: String(row.id),
    actor: row.actor_name ? { name: row.actor_name, hueKey: row.actor_id ?? undefined } : undefined,
    sentence: (
      <>
        <span className="mc-mono">{row.tool}</span>
        {row.summary ? <> · {row.summary}</> : null}
        {row.outcome !== "ok" ? <> · <span className="mc-mono">{row.refusal_reason ?? row.outcome}</span></> : null}
      </>
    ),
    at: row.at,
    tone: row.outcome === "ok" ? "info" : "red",
    source: row.source ?? undefined,
    artifacts,
    audit: changed || row.reason
      ? { old: changeSide(row.changes, "before"), new: changeSide(row.changes, "after"), reason: row.reason ?? undefined }
      : undefined,
  };
}

export interface LedgerCheckProps {
  result: AdminVerifyLedgerOutput | null;
  busy?: boolean | undefined;
  error?: string | null | undefined;
  onValidate?: (() => void) | undefined;
}

/** The hash chain, recomputed on demand: *Ready* when every row still chains, *Validation Failed* with the first row
 * that does not ("Audit is append-only: …"). */
export function LedgerCheck({ result, busy, error, onValidate }: LedgerCheckProps) {
  const items: [ReactNode, ReactNode][] = result
    ? [
        [SYSTEM_LABELS.status, <Pill key="s">{result.intact ? INTAKE_STATUS.ready : INTAKE_STATUS.validationFailed}</Pill>],
        [HISTORY_LABELS.audit, <span key="n" className="mc-mono">{result.rows_checked}</span>],
        ...(result.first_bad_id !== null
          ? [[HISTORY_LABELS.evidence, <span key="b" className="mc-mono">{result.first_bad_id}</span>] as [ReactNode, ReactNode]]
          : []),
      ]
    : [];
  return (
    <Card
      title={HISTORY_LABELS.audit}
      sub={AUDIT_ENGINE_LABELS.appendOnly}
      actions={<Button sm icon="check" busy={busy} onClick={onValidate} >{ACTION_LABELS.validate}</Button>}
      error={error ? { message: error, onRetry: onValidate } : undefined}
    >
      {result ? <KeyValue items={items} cols={3} /> : null}
    </Card>
  );
}

interface AuditFilter {
  actor: string;
  related: string;
  source: string;
}

const EMPTY_FILTER: AuditFilter = { actor: "", related: "", source: "" };

/** The query the filter asks for (blank fields left out). */
export function auditInput(filter: AuditFilter): AuditQueryInput {
  return {
    ...(filter.actor.trim() ? { actor_id: filter.actor.trim() } : {}),
    ...relatedFilter(filter.related),
    ...(filter.source.trim() ? { source: filter.source.trim() } : {}),
    limit: 100,
  };
}

function download(name: string, text: string) {
  const url = URL.createObjectURL(new Blob([text], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

function AuditEngine() {
  const [draft, setDraft] = useState<AuditFilter>(EMPTY_FILTER);
  const [asked, setAsked] = useState<AuditFilter>(EMPTY_FILTER);
  const [check, setCheck] = useState<{ result: AdminVerifyLedgerOutput | null; busy: boolean; error: string | null }>({ result: null, busy: false, error: null });
  const [exporting, setExporting] = useState(false);
  // audit.query is an audited read: one receipt per query, so it runs on Drill down, never per keystroke
  const q = useRead(["cfg", "audit.query", asked], () => auditQuery(auditInput(asked)), { staleTime: 0 });

  const validate = () => {
    setCheck((c) => ({ ...c, busy: true, error: null }));
    adminVerifyLedger({}).then(
      (result) => setCheck({ result, busy: false, error: null }),
      (e: unknown) => setCheck({ result: null, busy: false, error: errorText(e) }),
    );
  };
  const submit = (event: FormEvent) => {
    event.preventDefault();
    setAsked(draft);
  };
  const exportCsv = () => {
    setExporting(true);
    auditQuery({ ...auditInput(asked), format: "csv", limit: 1000 }).then(
      (out) => { if (out.csv) download("audit.csv", out.csv); setExporting(false); },
      () => setExporting(false),
    );
  };
  const set = (key: keyof AuditFilter) => (event: { target: { value: string } }) => setDraft((d) => ({ ...d, [key]: event.target.value }));

  return (
    <Stack>
      <LedgerCheck result={check.result} busy={check.busy} error={check.error} onValidate={validate} />
      <Card
        title={SYSTEM_LABELS.auditEngine}
        sub={AUDIT_ENGINE_LABELS.appendOnly}
        error={q.error ? { message: errorText(q.error), onRetry: () => void q.refetch(), details: { correlationId: q.error.requestId ?? undefined, status: q.error.status } } : undefined}
      >
        <form onSubmit={submit}>
          <Flex>
            <Field label={HISTORY_LABELS.actor}><Input mono sm value={draft.actor} onChange={set("actor")}  /></Field>
            <Field label={AUDIT_ENGINE_LABELS.relatedReqCandidate}><Input mono sm value={draft.related} onChange={set("related")}  /></Field>
            <Field label={HISTORY_LABELS.source}><Input mono sm value={draft.source} onChange={set("source")}  /></Field>
            <Button sm type="submit" icon="search" >{ACTION_LABELS.drillDown}</Button>
            <Button sm tone="ghost" icon="file" busy={exporting} onClick={exportCsv} >{ACTION_LABELS.export}</Button>
          </Flex>
        </form>
        <History
          label={HISTORY_LABELS.audit}
          events={(q.data?.items ?? []).map(auditEvent)}
          loading={q.isLoading ? 5 : undefined}
        />
      </Card>
    </Stack>
  );
}

// ---- SSO and Credentials ---------------------------------------------------------------------------------------------

const lastCall = (row: AdapterRow): string | null => {
  const at = row.health?.["last_call_at"];
  return typeof at === "string" ? at : null;
};
const lastError = (row: AdapterRow): string | null => {
  const kind = row.health?.["last_error_class"];
  return typeof kind === "string" ? kind : null;
};

/** The identity port, read-only: who signs people in, how it is wired and when it last answered. */
export function SsoView({ adapters, loading }: { adapters: readonly AdapterRow[]; loading?: boolean | undefined }) {
  const identity = adapters.find((row) => row.port === "identity");
  return (
    <Card title={SYSTEM_LABELS.sso} loading={loading ? { lines: 3 } : undefined} >
      <Stack>
        <Banner tone="info" icon="lock">{SYSTEM_LABELS.systemAdminControlled}</Banner>
        {identity ? (
          <KeyValue
            cols={2}
            items={[
              [SYSTEM_LABELS.integration, <span key="p" className="mc-mono">{identity.port}</span>],
              [HISTORY_LABELS.source, <span key="a" className="mc-mono">{identity.adapter}</span>],
              [SYSTEM_LABELS.status, <span key="m" className="mc-mono">{identity.mode}</span>],
              [ERROR_LABELS.lastAttempt, lastCall(identity) ? formatDateTime(lastCall(identity)!) : "—"],
            ]}
          />
        ) : loading ? null : <EmptyState inline>—</EmptyState>}
      </Stack>
    </Card>
  );
}

/** Secrets by name, presence and source — never a value — and every port's adapter, read-only. */
export function CredentialsView({ secrets, adapters, loading }: { secrets: readonly SecretRow[]; adapters: readonly AdapterRow[]; loading?: boolean | undefined }) {
  return (
    <Stack>
      <Card title={SYSTEM_LABELS.credentials} flush >
        <Table
          dense
          label={SYSTEM_LABELS.credentials}
          loading={loading ? 3 : undefined}
          head={<Tr><Th>{G6_LABELS.name}</Th><Th>{SYSTEM_LABELS.status}</Th><Th>{HISTORY_LABELS.source}</Th></Tr>}
        >
          {secrets.map((row) => (
            <Tr key={row.name}>
              <Td mono label={G6_LABELS.name}>{row.name}</Td>
              <Td label={SYSTEM_LABELS.status}><Pill tone={row.set ? "ok" : "red"}>{row.set ? ACCESS_LABELS.Yes : ACCESS_LABELS.No}</Pill></Td>
              <Td mono label={HISTORY_LABELS.source}>{row.source}</Td>
            </Tr>
          ))}
        </Table>
      </Card>
      <Card title={SYSTEM_LABELS.integrations} flush >
        <Table
          dense
          label={SYSTEM_LABELS.integrations}
          loading={loading ? 3 : undefined}
          head={(
            <Tr>
              <Th>{SYSTEM_LABELS.integration}</Th>
              <Th>{HISTORY_LABELS.source}</Th>
              <Th>{SYSTEM_LABELS.status}</Th>
              <Th>{ERROR_LABELS.lastAttempt}</Th>
              <Th>{ERROR_LABELS.errorDetails}</Th>
            </Tr>
          )}
        >
          {adapters.map((row) => (
            <Tr key={row.port}>
              <Td mono label={SYSTEM_LABELS.integration}>{row.port}</Td>
              <Td mono label={HISTORY_LABELS.source}>{row.adapter}</Td>
              <Td mono label={SYSTEM_LABELS.status}>{row.mode}</Td>
              <Td label={ERROR_LABELS.lastAttempt} nowrap>{lastCall(row) ? formatDateTime(lastCall(row)!) : "—"}</Td>
              <Td mono label={ERROR_LABELS.errorDetails}>{lastError(row) ?? "—"}</Td>
            </Tr>
          ))}
        </Table>
      </Card>
    </Stack>
  );
}

function SystemReads({ area }: { area: "sso" | "credentials" }) {
  const adapters = useRead(["cfg", "admin.adapters"], () => adminAdapters({}));
  const secrets = useRead(["cfg", "admin.secrets"], () => adminSecrets({}), { enabled: area === "credentials" });
  const failed = adapters.error ?? secrets.error;
  if (failed) {
    return <Card title={SYSTEM_AREA_LABEL[area]} error={{ message: errorText(failed), onRetry: () => { void adapters.refetch(); void secrets.refetch(); } }} />;
  }
  if (area === "sso") return <SsoView adapters={adapters.data?.items ?? []} loading={adapters.isLoading} />;
  return <CredentialsView secrets={secrets.data?.items ?? []} adapters={adapters.data?.items ?? []} loading={adapters.isLoading || secrets.isLoading} />;
}

function PermissionsEngine() {
  const q = useMatrix();
  return <PermissionMatrixView matrix={q.data} loading={q.isLoading} error={q.error} onRetry={() => void q.refetch()} />;
}

// ---- the screen -------------------------------------------------------------------------------------------------------

export interface SystemAdminFrameProps {
  route: Route;
  area: SystemArea | null;
  /** `#admin/integrations/<is_…>`: the integration row the link names. */
  focus?: string | undefined;
}

/** The area's frame: header, the section tabs, the section. A section the reader may not open is not found. */
export function SystemAdminFrame({ route, area, focus }: SystemAdminFrameProps) {
  const { can } = useSession();
  const canIntegrations = can("admin.integrations");
  if (area === null || (area === "integrations" && !canIntegrations)) return <V1Slot><NotFound route={route.raw} /></V1Slot>;
  return (
    <Stack>
      <PageHeader title={SYSTEM_LABELS.systemAdmin} meta={[SYSTEM_LABELS.systemAdminControlled]} />
      <Tabs route label={SYSTEM_LABELS.systemAdmin} items={systemTabs(canIntegrations)} active={area} />
      {area === "permissions" ? <PermissionsEngine /> : null}
      {area === "audit" ? <AuditEngine /> : null}
      {area === "sso" || area === "credentials" ? <SystemReads area={area} /> : null}
      {area === "integrations" ? <IntegrationsView focus={focus} /> : null}
    </Stack>
  );
}

/** `#settings/system` and `#settings/system/<area>`. */
export function SystemAdmin({ route }: { route: Route }) {
  return <SystemAdminFrame route={route} area={systemAreaOf(route)} />;
}

/** `#admin/integrations/<is_…>` (B11 C-B11-11: a failed sync's recovery task links here): the Integrations section
 * with that row first. */
export function SystemAdminIntegrations({ route }: { route: Route }) {
  return <SystemAdminFrame route={route} area="integrations" focus={route.parts[1]} />;
}

